// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import styles from '../styles/gameStyles';
import { currencyManager } from '../collection';
import { packSystemServer } from '../collection/systems/packSystemServer';
import { TournamentViewer } from '../collection';
import { useAuth } from '../lib/useAuth';

/**
 * GameOverScreen - ゲーム終了画面コンポーネント
 *
 * Phase 1: magic-spirit.jsxからの画面分離
 *
 * @param {Object} props
 * @param {1|2} props.winner - 勝利プレイヤー
 * @param {Object|null} props.battleReward - 対戦報酬 { goldReward, packReward, isWin }
 * @param {function} props.setBattleReward - 報酬リセット関数
 * @param {Object|null} props.playerData - プレイヤーデータ
 * @param {Array} props.allCards - 全カードデータ
 * @param {Map} props.cardValueMap - カード価値マップ
 * @param {boolean} props.showTournamentViewer - 大会観戦ダイアログ表示
 * @param {Object|null} props.pendingTournamentResult - 報酬待ち大会結果
 * @param {function} props.setShowTournamentViewer - 大会観戦ダイアログ表示setter
 * @param {function} props.setPendingTournamentResult - 報酬待ち大会結果setter
 * @param {function} props.awardBattleRewards - 報酬付与関数
 * @param {function} props.updatePlayerData - プレイヤーデータ更新関数
 * @param {function} props.handleOpenPack - パック開封ハンドラ
 * @param {function} props.handleClaimTournamentReward - 大会報酬請求ハンドラ
 * @param {function} props.onPlayAgain - もう一度プレイ
 * @param {function} props.onGoToMenu - メニューへ戻る
 */
const GameOverScreen = ({
  winner,
  battleReward,
  setBattleReward,
  playerData,
  allCards,
  cardValueMap,
  showTournamentViewer,
  pendingTournamentResult,
  setShowTournamentViewer,
  setPendingTournamentResult,
  awardBattleRewards,
  updatePlayerData,
  handleOpenPack,
  handleClaimTournamentReward,
  onPlayAgain,
  onGoToMenu,
}) => {
  const { session } = useAuth();
  const accessToken = session?.access_token;
  const [isProcessing, setIsProcessing] = useState(false);

  const attemptedReward = useRef(false);
  const [rewardError, setRewardError] = useState(null);
  const claimReward = async () => {
    if (attemptedReward.current) return;
    attemptedReward.current = true; setRewardError(null);
    try { await awardBattleRewards(winner === 1); }
    catch (error) { setRewardError(error.message || '報酬を保存できませんでした。'); }
  };
  useEffect(() => {
    if (!battleReward && playerData) claimReward();
  }, [battleReward, playerData, winner, awardBattleRewards]);

  const handleOpenUnopenedPack = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const result = await packSystemServer.openUnopenedPackAsync(playerData, allCards, cardValueMap, accessToken);
      if (result.success) {
        updatePlayerData(result.playerData);
        handleOpenPack(result.cards);
      } else {
        console.error('[GameOverScreen] Pack open failed:', result.error);
      }
    } catch (err) {
      console.error('[GameOverScreen] Pack open error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoToMenu = () => {
    setBattleReward(null);
    onGoToMenu();
  };

  return (
    <div style={styles.container}>
      <div style={styles.modal}>
        <div style={styles.modalContent}>
          <h2 style={{ textAlign: 'center', marginBottom: '24px', color: '#ffd700' }}>
            🏆 ゲーム終了 🏆
          </h2>
          <p style={{ textAlign: 'center', fontSize: '24px', marginBottom: '16px' }}>
            プレイヤー{winner}の勝利！
          </p>

          {!battleReward && <div role="status" style={{ textAlign: 'center', margin: 16 }}>
            {rewardError || '報酬を保存しています…'}
            {rewardError && <button onClick={() => { attemptedReward.current = false; claimReward(); }}>受取を再試行</button>}
          </div>}
          {battleReward?.rewardLimited && <p>本日の報酬上限に達しました。対戦は引き続き遊べます。</p>}
          {/* 報酬表示 */}
          {battleReward && (
            <div style={{
              background: 'rgba(255,215,0,0.1)',
              border: '1px solid rgba(255,215,0,0.3)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '16px', color: '#ffd700', marginBottom: '12px', fontWeight: 'bold' }}>
                ───── 報酬 ─────
              </div>
              <div style={{ fontSize: '18px', color: '#ffd700', marginBottom: '8px' }}>
                💰 {currencyManager.formatGold(battleReward.goldReward || 0)}
              </div>
              {battleReward.packReward > 0 && (
                <div style={{ fontSize: '16px', color: '#ff9500' }}>
                  🎴 パック ×{battleReward.packReward}（勝利ボーナス）
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            {/* 未開封パック開封ボタン */}
            {playerData?.unopenedPacks > 0 && (
              <button
                onClick={handleOpenUnopenedPack}
                disabled={isProcessing}
                style={{
                  ...styles.actionButton,
                  background: isProcessing
                    ? 'linear-gradient(135deg, #666 0%, #888 100%)'
                    : 'linear-gradient(135deg, #ff9500 0%, #ffd700 100%)',
                  color: '#1a1a2e',
                  fontSize: '16px',
                  padding: '12px 32px',
                  opacity: isProcessing ? 0.7 : 1,
                  cursor: isProcessing ? 'wait' : 'pointer',
                }}
              >
                {isProcessing ? '⏳ 開封中...' : `🎴 パックを開ける (${playerData.unopenedPacks}個)`}
              </button>
            )}

            <div style={{ display: 'flex', gap: '16px' }}>
              <button disabled={!battleReward} onClick={onPlayAgain} style={styles.actionButton}>
                もう一度プレイ
              </button>
              <button
                disabled={!battleReward}
                onClick={handleGoToMenu}
                style={{ ...styles.actionButton, background: '#444' }}
              >
                メニューへ
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 大会観戦ダイアログ（ゲームオーバー画面でも表示） */}
      {showTournamentViewer && pendingTournamentResult && (
        <TournamentViewer
          tournament={pendingTournamentResult}
          currentBets={playerData?.tournamentData?.currentBets || []}
          onClaimReward={handleClaimTournamentReward}
          onClose={() => {
            setShowTournamentViewer(false);
            setPendingTournamentResult(null);
          }}
        />
      )}
    </div>
  );
};

export default GameOverScreen;
